/**
 * Centralized storage for AbortControllers used in uploads
 * This is stored outside Redux because AbortController instances are not serializable
 */

import logger from '../utils/logger';

const uploadAbortControllers = new Map();

// Store File objects outside Redux (File objects are not serializable)
const uploadFileObjects = new Map();

// Store upload session data (S3 keys and upload_ids) for abort API
const uploadSessionData = new Map();

export const setUploadAbortController = (uploadId, abortController) => {
    uploadAbortControllers.set(uploadId, abortController);
};

export const getUploadAbortController = (uploadId) => {
    return uploadAbortControllers.get(uploadId);
};

export const abortUpload = (uploadId) => {
    let aborted = false;
    
    // 1. Abort main orchestrator controller (for S3 uploads)
    const controller = uploadAbortControllers.get(uploadId);
    if (controller) {
        controller.abort();
        uploadAbortControllers.delete(uploadId);
        logger.debug(`✅ Aborted main upload controller: ${uploadId}`);
        aborted = true;
    }
    
    // 2. Abort all batch-specific controllers (Phase 1 init and Phase 2 start-upload)
    // Find all keys that start with uploadId__ (batch-specific controllers)
    const allKeys = Array.from(uploadAbortControllers.keys());
    const batchKeys = allKeys.filter(key => key.startsWith(`${uploadId}__`));
    
    batchKeys.forEach(key => {
        const batchController = uploadAbortControllers.get(key);
        if (batchController) {
            batchController.abort();
            uploadAbortControllers.delete(key);
            logger.debug(`✅ Aborted batch controller: ${key}`);
            aborted = true;
        }
    });
    
    if (!aborted) {
        console.warn(`⚠️ No AbortController found for upload: ${uploadId}`);
    }
    
    return aborted;
};

export const removeUploadAbortController = (uploadId) => {
    uploadAbortControllers.delete(uploadId);
};

export const clearAllAbortControllers = () => {
    uploadAbortControllers.forEach((controller, uploadId) => {
        controller.abort();
        logger.debug(`✅ Aborted upload: ${uploadId}`);
    });
    uploadAbortControllers.clear();
};

// File objects management
export const setUploadFileObjects = (uploadId, files) => {
    uploadFileObjects.set(uploadId, files);
    logger.debug(`📁 Stored ${files.length} file objects for upload: ${uploadId}`);
};

export const getUploadFileObjects = (uploadId) => {
    return uploadFileObjects.get(uploadId);
};

export const removeUploadFileObjects = (uploadId) => {
    uploadFileObjects.delete(uploadId);
    logger.debug(`🗑️ Removed file objects for upload: ${uploadId}`);
};

export const clearAllFileObjects = () => {
    uploadFileObjects.clear();
};

// Upload session data management (for abort API)
export const setUploadSessionData = (uploadId, sessionData) => {
    uploadSessionData.set(uploadId, sessionData);
    logger.debug(`📋 Stored session data for upload: ${uploadId}`);
};

export const getUploadSessionData = (uploadId) => {
    return uploadSessionData.get(uploadId);
};

export const addFileSessionData = (uploadId, fileSessionData) => {
    const currentData = uploadSessionData.get(uploadId) || [];
    currentData.push(fileSessionData);
    uploadSessionData.set(uploadId, currentData);
    logger.debug(`📋 Added file session data for upload: ${uploadId}`, fileSessionData);
};

export const removeUploadSessionData = (uploadId) => {
    uploadSessionData.delete(uploadId);
    logger.debug(`🗑️ Removed session data for upload: ${uploadId}`);
};

export const clearAllSessionData = () => {
    uploadSessionData.clear();
};

// Upload context management (stores metadata needed for API calls during pause/resume)
const uploadContextData = new Map();

export const setUploadContext = (uploadId, uploadContext) => {
    uploadContextData.set(uploadId, uploadContext);
    logger.debug(`⚙️ Stored upload context for upload: ${uploadId}`, uploadContext);
};

export const getUploadContext = (uploadId) => {
    return uploadContextData.get(uploadId);
};

export const removeUploadContext = (uploadId) => {
    uploadContextData.delete(uploadId);
    logger.debug(`🗑️ Removed upload context for upload: ${uploadId}`);
};

export const clearAllUploadContexts = () => {
    uploadContextData.clear();
};
